import {
  KeenSliderHooks,
  KeenSliderInstance,
  KeenSliderPlugin,
  useKeenSlider,
} from 'keen-slider/react';
import { useCallback, useEffect, useState } from 'preact/compat';

import { KeyboardPlugin } from './keen-keyboard-plugin';
import { ResizePlugin } from './keen-resize-plugin';
import { ThumbnailPlugin } from './keen-thumbnail-plugin';

import styles from '../ImageSlider.module.scss';

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      slider.update();
    });
  });
  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

export function useSlider(initial = 0) {
  const [hasNext, setHasNext] = useState(true);
  const [hasPrev, setHasPrev] = useState(false);

  function updateControls(
    instance: KeenSliderInstance<any, any, KeenSliderHooks>,
  ) {
    setHasNext(instance.slides.length > instance.track.details.rel + 1);
    setHasPrev(instance.track.details.rel > 0);
  }

  const [sliderRef, slider] = useKeenSlider(
    {
      initial,
      selector: `.${styles.Slide}`,
      created: updateControls,
      slideChanged: updateControls,
    },
    [ResizePlugin, KeyboardPlugin, MutationPlugin],
  );

  const [thumbnailRef, thumbnails] = useKeenSlider(
    {
      initial,
      selector: `.${styles.Thumbnail}`,
      slides: {
        perView: 'auto',
        origin: 'center',
        spacing: 20,
      },
      drag: false,
    },
    [ThumbnailPlugin(slider), MutationPlugin],
  );

  useEffect(() => {
    slider.current && slider.current.update();
    thumbnails.current && thumbnails.current.update();
  }, [slider, thumbnails, sliderRef, thumbnailRef]);

  const next = useCallback(() => {
    slider.current?.next();
  }, [slider]);

  const prev = useCallback(() => {
    slider.current?.prev();
  }, [slider]);

  return {
    sliderRef,
    thumbnailRef,
    next,
    prev,
    hasNext,
    hasPrev,
  };
}
